import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Check,
  Phone,
  MapPin,
  Clock,
  ArrowRight,
  Star,
  Shield,
  TrendingUp,
  Award,
  Clock3,
  // ChevronUp,
  ChevronDown,
  Calculator,
  Percent,
  Wallet,
  FileCheck,
  Users,
  Sparkles,
  Zap,
  Lock,
  HeartHandshake,
  BarChart3,
  PiggyBank,
  BadgeCheck,
  ThumbsUp,
  HelpCircle,
  Minus,
  Plus,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import "./FinanceCenter.css";

// Scroll reveal hook
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );

    document.querySelectorAll(".scroll-reveal").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
}

// Counter animation hook
// function useCounter(end: number, duration: number = 2000) {
//   const [count, setCount] = useState(0);
//   const [isVisible, setIsVisible] = useState(false);
//   const ref = useRef<HTMLDivElement>(null);

//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting && !isVisible) {
//           setIsVisible(true);
//         }
//       },
//       { threshold: 0.5 },
//     );

//     if (ref.current) {
//       observer.observe(ref.current);
//     }

//     return () => observer.disconnect();
//   }, [isVisible]);

//   useEffect(() => {
//     if (!isVisible) return;

//     let startTime: number;
//     const animate = (currentTime: number) => {
//       if (!startTime) startTime = currentTime;
//       const progress = Math.min((currentTime - startTime) / duration, 1);
//       setCount(Math.floor(progress * end));
//       if (progress < 1) {
//         requestAnimationFrame(animate);
//       }
//     };
//     requestAnimationFrame(animate);
//   }, [isVisible, end, duration]);

//   return { count, ref };
// }

// FAQ Item Component
function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 pr-4">{question}</span>
        <div
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${isOpen ? "bg-red-600 text-white" : "bg-gray-100 text-gray-600"}`}
        >
          {isOpen ? (
            <Minus className="w-4 h-4" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-96" : "max-h-0"}`}
      >
        <p className="px-5 pb-5 text-gray-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

type LoanFormValues = {
  vehiclePrice: number;
  downPayment: number;
  tradeInValue: number;
  existingLoanBalance: number;
  interestRate: number;
  loanTermMonths: number;
  salesTaxPercent: number;
  targetMonthlyPayment: number;
};

type CalculationMode = "vehiclePrice" | "monthlyPayment";

const DEFAULT_LOAN_VALUES: LoanFormValues = {
  vehiclePrice: 35000,
  downPayment: 5000,
  tradeInValue: 2000,
  existingLoanBalance: 0,
  interestRate: 7.5,
  loanTermMonths: 72,
  salesTaxPercent: 8,
  targetMonthlyPayment: 650,
};

function sanitizeNumber(value: number): number {
  if (!Number.isFinite(value) || Number.isNaN(value)) {
    return 0;
  }
  return Math.max(0, value);
}

function roundToTwo(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function calculateFrequencyMetrics(
  principal: number,
  annualInterestRate: number,
  loanTermMonths: number,
  periodsPerYear: number,
) {
  if (loanTermMonths <= 0 || principal <= 0) {
    return { paymentAmount: 0, totalInterest: 0 };
  }

  const years = loanTermMonths / 12;
  const totalPeriods = years * periodsPerYear;
  const periodRate = annualInterestRate / 100 / periodsPerYear;

  if (totalPeriods <= 0) {
    return { paymentAmount: 0, totalInterest: 0 };
  }

  let paymentAmount = 0;
  if (annualInterestRate > 0) {
    const growthFactor = Math.pow(1 + periodRate, totalPeriods);
    const denominator = growthFactor - 1;
    paymentAmount =
      denominator === 0
        ? principal / totalPeriods
        : (principal * periodRate * growthFactor) / denominator;
  } else {
    paymentAmount = principal / totalPeriods;
  }

  const rawPaymentAmount = paymentAmount;
  paymentAmount = roundToTwo(rawPaymentAmount);
  const totalInterest = roundToTwo(rawPaymentAmount * totalPeriods - principal);

  return { paymentAmount, totalInterest };
}

function calculateLoanMetrics(values: LoanFormValues) {
  const {
    vehiclePrice,
    downPayment,
    tradeInValue,
    existingLoanBalance,
    interestRate,
    loanTermMonths,
    salesTaxPercent,
  } = values;

  const numericInputs: Record<string, number> = {
    vehicle_price: vehiclePrice,
    down_payment: downPayment,
    trade_in_value: tradeInValue,
    existing_loan_balance: existingLoanBalance,
    interest_rate: interestRate,
    loan_term_months: loanTermMonths,
    sales_tax_percent: salesTaxPercent,
  };

  Object.entries(numericInputs).forEach(([key, value]) => {
    if (!Number.isFinite(value)) {
      throw new Error(`Invalid input: ${key} must be a finite number.`);
    }
    if (value < 0) {
      throw new Error(`Invalid input: ${key} cannot be negative.`);
    }
  });

  const normalizedLoanTerm = Math.floor(loanTermMonths || 0);
  if (normalizedLoanTerm <= 0) {
    return {
      monthlyPayment: 0,
      totalPayment: 0,
      totalInterest: 0,
      principal: 0,
      taxAmount: 0,
      biWeeklyPayment: 0,
      weeklyPayment: 0,
    };
  }

  // Step 1: Taxable amount after trade-in credit (never below 0)
  const taxableAmount = Math.max(0, vehiclePrice - tradeInValue);
  // Step 2: Sales tax
  const taxAmount = taxableAmount * (salesTaxPercent / 100);
  // Step 3: Total cost including tax
  const totalCost = vehiclePrice + taxAmount;
  // Step 4: Principal with rolled balance, clamped at 0
  const principal = Math.max(
    0,
    totalCost - downPayment - tradeInValue + existingLoanBalance,
  );
  // Step 6: Monthly interest rate
  const monthlyRate = interestRate / 100 / 12;

  // Step 7: Monthly payment (EMI)
  let monthlyPayment = 0;
  if (interestRate > 0) {
    const growthFactor = Math.pow(1 + monthlyRate, normalizedLoanTerm);
    const denominator = growthFactor - 1;

    if (denominator === 0) {
      throw new Error("Calculation error: division by zero in EMI formula.");
    }

    monthlyPayment = (principal * monthlyRate * growthFactor) / denominator;
  } else {
    monthlyPayment = principal / normalizedLoanTerm;
  }

  // Step 8: Round core payment
  const rawMonthlyPayment = monthlyPayment;
  monthlyPayment = roundToTwo(rawMonthlyPayment);

  // Step 9: Totals
  const totalPayment = roundToTwo(rawMonthlyPayment * normalizedLoanTerm);
  const totalInterest = roundToTwo(totalPayment - principal);
  // Step 10: Additional breakdown
  const biWeeklyPayment = calculateFrequencyMetrics(
    principal,
    interestRate,
    normalizedLoanTerm,
    26,
  ).paymentAmount;
  const weeklyPayment = calculateFrequencyMetrics(
    principal,
    interestRate,
    normalizedLoanTerm,
    52,
  ).paymentAmount;

  return {
    monthlyPayment,
    totalPayment,
    totalInterest,
    principal,
    taxAmount: roundToTwo(taxAmount),
    biWeeklyPayment,
    weeklyPayment,
  };
}

function LoanCalculator() {
  const [formValues, setFormValues] =
    useState<LoanFormValues>(DEFAULT_LOAN_VALUES);
  const [mode, setMode] = useState<CalculationMode>("vehiclePrice");

  const calculations = useMemo(
    () => calculateLoanMetrics(formValues),
    [formValues],
  );

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [],
  );

  const setNumericField = (key: keyof LoanFormValues, rawValue: string) => {
    const parsedValue = rawValue === "" ? 0 : Number(rawValue);
    setFormValues((previousValues) => ({
      ...previousValues,
      [key]: sanitizeNumber(parsedValue),
    }));
  };

  const handleModeChange = (nextMode: CalculationMode) => {
    setMode(nextMode);
    if (nextMode === "monthlyPayment") {
      setFormValues((previousValues) => ({
        ...previousValues,
        targetMonthlyPayment: Number(calculations.monthlyPayment.toFixed(2)),
      }));
    }
  };

  const estimatedVehiclePriceFromTarget = useMemo(() => {
    if (mode !== "monthlyPayment") {
      return formValues.vehiclePrice;
    }

    const taxFactor = 1 + formValues.salesTaxPercent / 100;
    const rolledLoan = formValues.existingLoanBalance;
    const term = Math.max(1, Math.floor(formValues.loanTermMonths));
    const taxRate = formValues.salesTaxPercent / 100;
    const monthlyRate = formValues.interestRate / 100 / 12;
    const targetMonthly = sanitizeNumber(formValues.targetMonthlyPayment);

    let principal = 0;

    if (monthlyRate > 0) {
      const growth = Math.pow(1 + monthlyRate, term);
      principal = (targetMonthly * (growth - 1)) / (monthlyRate * growth);
    } else {
      principal = targetMonthly * term;
    }

    const estimatedVehiclePriceNumerator =
      principal +
      formValues.downPayment -
      rolledLoan +
      formValues.tradeInValue * (1 + taxRate);

    const estimatedVehiclePrice =
      estimatedVehiclePriceNumerator / Math.max(taxFactor, 0.0001);

    return sanitizeNumber(estimatedVehiclePrice);
  }, [formValues, mode]);

  const displayedVehiclePrice =
    mode === "monthlyPayment"
      ? estimatedVehiclePriceFromTarget
      : formValues.vehiclePrice;

  const displayedCalculations =
    mode === "monthlyPayment"
      ? calculateLoanMetrics({
          ...formValues,
          vehiclePrice: displayedVehiclePrice,
        })
      : calculations;

  const paymentBreakdownRows = useMemo(() => {
    const monthlyMetrics = calculateFrequencyMetrics(
      displayedCalculations.principal,
      formValues.interestRate,
      formValues.loanTermMonths,
      12,
    );
    const biWeeklyMetrics = calculateFrequencyMetrics(
      displayedCalculations.principal,
      formValues.interestRate,
      formValues.loanTermMonths,
      26,
    );
    const weeklyMetrics = calculateFrequencyMetrics(
      displayedCalculations.principal,
      formValues.interestRate,
      formValues.loanTermMonths,
      52,
    );

    return [
      {
        frequency: "Monthly",
        paymentAmount: monthlyMetrics.paymentAmount,
        totalInterest: monthlyMetrics.totalInterest,
      },
      {
        frequency: "Bi-weekly",
        paymentAmount: biWeeklyMetrics.paymentAmount,
        totalInterest: biWeeklyMetrics.totalInterest,
      },
      {
        frequency: "Weekly",
        paymentAmount: weeklyMetrics.paymentAmount,
        totalInterest: weeklyMetrics.totalInterest,
      },
    ];
  }, [
    displayedCalculations.principal,
    formValues.interestRate,
    formValues.loanTermMonths,
  ]);

  const targetVehiclePriceWithTax = useMemo(() => {
    const taxableAmount = Math.max(
      0,
      displayedVehiclePrice - formValues.tradeInValue,
    );
    const taxAmount = taxableAmount * (formValues.salesTaxPercent / 100);
    return roundToTwo(displayedVehiclePrice + taxAmount);
  }, [
    displayedVehiclePrice,
    formValues.tradeInValue,
    formValues.salesTaxPercent,
  ]);

  return (
    <section
      id="auto-loan-calculator"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24"
    >
      <div className="scroll-reveal bg-white rounded-3xl shadow-xl border border-gray-200 p-6 md:p-8 lg:p-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <Badge className="mb-3 bg-red-100 text-red-700 border-0">
              <Calculator className="w-4 h-4 mr-2" />
              Auto Loan Calculator
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Estimate Your Vehicle Financing
            </h2>
          </div>
          <button
            type="button"
            onClick={() => {
              setFormValues(DEFAULT_LOAN_VALUES);
              setMode("vehiclePrice");
            }}
            className="inline-flex items-center rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Reset
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-5">
            <div className="bg-gray-100 rounded-xl p-1 inline-flex gap-1">
              {[
                {
                  label: "Calculate by vehicle price",
                  value: "vehiclePrice" as const,
                },
                {
                  label: "Calculate by monthly payment",
                  value: "monthlyPayment" as const,
                },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleModeChange(option.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    mode === option.value
                      ? "bg-white text-red-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <NumberSliderField
              label="Vehicle Price"
              value={formValues.vehiclePrice}
              min={0}
              max={150000}
              step={100}
              disabled={mode === "monthlyPayment"}
              onChange={(value) => setNumericField("vehiclePrice", value)}
              prefix="$"
            />
            {mode === "monthlyPayment" && (
              <p className="text-sm text-gray-500 -mt-3">
                Estimated vehicle price based on your target payment:{" "}
                <span className="font-semibold text-gray-800">
                  {currencyFormatter.format(displayedVehiclePrice)}
                </span>
              </p>
            )}

            {mode === "monthlyPayment" && (
              <NumberSliderField
                label="Target Monthly Payment"
                value={formValues.targetMonthlyPayment}
                min={0}
                max={5000}
                step={10}
                onChange={(value) =>
                  setNumericField("targetMonthlyPayment", value)
                }
                prefix="$"
              />
            )}

            <NumberSliderField
              label="Down Payment"
              value={formValues.downPayment}
              min={0}
              max={100000}
              step={100}
              onChange={(value) => setNumericField("downPayment", value)}
              prefix="$"
            />
            <NumberSliderField
              label="Trade-in Value"
              value={formValues.tradeInValue}
              min={0}
              max={80000}
              step={100}
              onChange={(value) => setNumericField("tradeInValue", value)}
              prefix="$"
            />
            <NumberSliderField
              label="Existing Loan Balance"
              value={formValues.existingLoanBalance}
              min={0}
              max={80000}
              step={100}
              onChange={(value) =>
                setNumericField("existingLoanBalance", value)
              }
              prefix="$"
            />
            <NumberSliderField
              label="Interest Rate (%)"
              value={formValues.interestRate}
              min={0}
              max={25}
              step={0.1}
              onChange={(value) => setNumericField("interestRate", value)}
            />
            <NumberSliderField
              label="Loan Term (months)"
              value={formValues.loanTermMonths}
              min={12}
              max={96}
              step={1}
              onChange={(value) => setNumericField("loanTermMonths", value)}
            />
            <NumberSliderField
              label="Sales Tax (%)"
              value={formValues.salesTaxPercent}
              min={0}
              max={20}
              step={0.1}
              onChange={(value) => setNumericField("salesTaxPercent", value)}
            />
          </div>

          <div className="bg-gray-900 rounded-2xl p-6 md:p-8 text-white">
            {mode === "monthlyPayment" ? (
              <div className="space-y-4">
                <ResultCard
                  label="Target Vehicle Price (with tax)"
                  value={currencyFormatter.format(targetVehiclePriceWithTax)}
                />
                <ResultCard
                  label="Total Interest to be Paid over the Duration of the Loan"
                  value={currencyFormatter.format(
                    displayedCalculations.totalInterest,
                  )}
                />
              </div>
            ) : (
              <>
                <p className="text-sm uppercase tracking-wider text-gray-400 mb-2">
                  Estimated payment
                </p>
                <p className="text-4xl md:text-5xl font-bold text-green-400 mb-6">
                  {currencyFormatter.format(
                    displayedCalculations.monthlyPayment,
                  )}
                  <span className="text-lg text-gray-400 font-medium">
                    {" "}
                    /month
                  </span>
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ResultCard
                    label="Bi-weekly Payment"
                    value={currencyFormatter.format(
                      displayedCalculations.biWeeklyPayment,
                    )}
                  />
                  <ResultCard
                    label="Weekly Payment"
                    value={currencyFormatter.format(
                      displayedCalculations.weeklyPayment,
                    )}
                  />
                  <ResultCard
                    label="Total Interest"
                    value={currencyFormatter.format(
                      displayedCalculations.totalInterest,
                    )}
                  />
                  <ResultCard
                    label="Total Loan Amount"
                    value={currencyFormatter.format(
                      displayedCalculations.principal,
                    )}
                  />
                </div>

                <div className="mt-8">
                  <p className="text-sm font-semibold text-gray-300 mb-3">
                    Total Interest to be Paid over the Duration of the Loan
                  </p>
                  <div className="overflow-hidden rounded-xl border border-white/15">
                    <table className="w-full text-sm">
                      <thead className="bg-white/10">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-300">
                            Payment Frequency
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-gray-300">
                            Payment Amount
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-gray-300">
                            Total Interest
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentBreakdownRows.map((row) => (
                          <tr
                            key={row.frequency}
                            className="border-t border-white/10"
                          >
                            <td className="px-3 py-2 text-gray-100">
                              {row.frequency}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-100">
                              {currencyFormatter.format(row.paymentAmount)}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-100">
                              {currencyFormatter.format(row.totalInterest)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Square Trigger branding */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <a
            href="https://squaretrigger.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group mx-auto flex w-fit items-center justify-center gap-3 rounded-full border border-gray-200 bg-gray-50 px-5 py-3 text-center shadow-sm transition-all duration-300 hover:border-red-200 hover:bg-red-50"
          >
            <span className="text-sm sm:text-base text-gray-600 leading-tight">
              Designed with passion by{" "}
              <span className="font-semibold text-gray-900 group-hover:text-red-600 transition-colors">
                Square Trigger
              </span>
            </span>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden">
              <img
                src="/images/square-trigger-symbol.png"
                alt="Square Trigger"
                className="h-5 w-5 object-contain"
                loading="lazy"
              />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

type NumberSliderFieldProps = {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  prefix?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
};

function NumberSliderField({
  label,
  value,
  min,
  max,
  step,
  prefix,
  disabled = false,
  onChange,
}: NumberSliderFieldProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-semibold text-gray-700">{label}</label>
        <div className="relative w-36">
          {prefix && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
              {prefix}
            </span>
          )}
          <input
            type="number"
            inputMode="decimal"
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            value={Number.isFinite(value) ? value : 0}
            onChange={(event) => onChange(event.target.value)}
            className={`w-full rounded-lg border border-gray-300 bg-white py-2 text-sm ${
              prefix ? "pl-7 pr-3" : "px-3"
            } ${disabled ? "cursor-not-allowed bg-gray-100 text-gray-500" : ""}`}
          />
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        value={Number.isFinite(value) ? value : 0}
        onChange={(event) => onChange(event.target.value)}
        className="w-full accent-red-600 disabled:opacity-40"
      />
    </div>
  );
}

function ResultCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white/10 border border-white/15 rounded-xl p-4">
      <p className="text-sm text-gray-300 mb-1">{label}</p>
      <p className="text-xl font-semibold">{value}</p>
    </div>
  );
}

function Finance() {
  useScrollReveal();
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const targetId = location.hash.replace("#", "");
    const timeoutId = window.setTimeout(() => {
      const element = document.getElementById(targetId);
      if (!element) return;

      const offset = 120;
      const top =
        element.getBoundingClientRect().top + window.pageYOffset - offset;

      window.scrollTo({
        top,
        behavior: "smooth",
      });
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [location.hash]);

  // Counter stats
  // const approvalRate = useCounter(98, 2000);
  // const happyCustomers = useCounter(5000, 2500);
  // const yearsExperience = useCounter(19, 1500);
  // const sameDayApproval = useCounter(85, 2000);

  // FAQ data
  const faqs = [
    {
      question: "Can I still apply if I have bad credit?",
      answer:
        "Yes. We help customers with bad credit, low credit, and many different credit situations. Our team works with multiple lenders to help explore financing options that may fit your needs.",
    },
    {
      question: "What if I have no credit history in Canada?",
      answer:
        "You can still apply. We help customers who are new to Canada or just starting to build credit here. If you have steady income or full-time employment, our team will work hard to help you find an option.",
    },
    {
      question: "Do I need full-time employment to apply?",
      answer:
        "A steady income or full-time job can strengthen your application. It helps our team and lending partners better understand your ability to manage payments.",
    },
    {
      question: "How long does the approval process take?",
      answer:
        "Many customers receive a response quickly after applying. Timing can vary by situation, but our team works to make the process simple and efficient.",
    },
    {
      question: "What kinds of credit situations do you work with?",
      answer:
        "We work with customers who have bad credit, low credit, no credit, and new credit in Canada. All credit situations are welcome.",
    },
    {
      question: "What is the first step?",
      answer:
        "The first step is to apply online or contact our team directly. We will review your situation and help guide you through the next steps.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section - Stunning Design */}
      <section className="relative pt-20 min-h-[90vh] flex items-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <img
            src="/images/finance-hero-bg.jpg"
            alt="Finance office"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-gray-900/70 to-red-900/40" />

          {/* Animated Particles */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-30" />
            <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-yellow-500 rounded-full animate-pulse opacity-20" />
            <div className="absolute bottom-1/4 left-1/2 w-2 h-2 bg-white rounded-full animate-bounce opacity-20" />
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="scroll-reveal">
              <Badge className="mb-6 bg-gradient-to-r from-red-600 to-red-500 text-white border-0 px-4 py-2 text-sm">
                <Sparkles className="w-4 h-4 mr-2" />
                All Credit Welcome
              </Badge>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
                CREDIT CHALLENGES? <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-yellow-400 to-red-400 animate-gradient">
                  WE'VE GOT YOU COVERED.
                </span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 max-w-lg">
                Whether you have bad credit, low credit, or are new to Canada
                with no credit history, getting approved for a vehicle may still
                be possible.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <Link
                  to="/#financing"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg hover:shadow-red-500/30 transition-all hover:-translate-y-1"
                >
                  <FileCheck className="w-5 h-5" />
                  Apply for Financing
                  <ArrowRight className="w-5 h-5" />
                </Link>

                <Link
                  to="/car-inventory"
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/20 transition-all"
                >
                  <Calculator className="w-5 h-5" />
                  Explore Inventory
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap gap-6 text-white/80">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-400" />
                  <span className="text-sm">Bad Credit Welcome</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock3 className="w-5 h-5 text-yellow-400" />
                  <span className="text-sm">Low Credit Welcome</span>
                </div>
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-blue-400" />
                  <span className="text-sm">New to Canada Welcome</span>
                </div>
              </div>
            </div>

            {/* Right Content - Stats Cards */}
            {/* <div className="hidden lg:block scroll-reveal">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform hover:scale-105 transition-transform">
                  <div
                    ref={approvalRate.ref}
                    className="text-5xl font-bold text-white mb-2"
                  >
                    {approvalRate.count}%
                  </div>
                  <p className="text-gray-300">Approval Rate</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform hover:scale-105 transition-transform mt-8">
                  <div
                    ref={happyCustomers.ref}
                    className="text-5xl font-bold text-white mb-2"
                  >
                    {happyCustomers.count.toLocaleString()}+
                  </div>
                  <p className="text-gray-300">Happy Customers</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform hover:scale-105 transition-transform">
                  <div
                    ref={yearsExperience.ref}
                    className="text-5xl font-bold text-white mb-2"
                  >
                    {yearsExperience.count}+
                  </div>
                  <p className="text-gray-300">Years Experience</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform hover:scale-105 transition-transform mt-8">
                  <div
                    ref={sameDayApproval.ref}
                    className="text-5xl font-bold text-white mb-2"
                  >
                    {sameDayApproval.count}%
                  </div>
                  <p className="text-gray-300">Same-Day Approval</p>
                </div>
              </div>
            </div> */}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </section>

      {/* Main Content */}
      <main className="pt-28 pb-16">
        {/* Welcome Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="scroll-reveal relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/images/finance-consultation.jpg"
                  alt="Finance consultation"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent" />
              </div>

              {/* Floating Card */}
              <div className="absolute -bottom-8 -right-8 bg-white rounded-2xl shadow-xl p-6 max-w-xs hidden md:block">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <BadgeCheck className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Loan Approved!</p>
                    <p className="text-sm text-gray-500">Just now</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Congratulations! Your financing has been approved.
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="scroll-reveal">
              <Badge className="mb-4 bg-red-100 text-red-700 border-0">
                <HeartHandshake className="w-4 h-4 mr-2" />
                Finance Solutions for All Credit Types
              </Badge>

              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                All Credit <span className="text-red-600">Situations </span>{" "}
                Welcome
              </h2>

              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At Big Boys MotorClub, we work with multiple lenders to help
                customers with all types of credit situations. Whether you have
                bad credit, low credit, no credit, or are just starting to build
                credit in Canada, our team is here to help.
              </p>

              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                If you have a steady income or full-time job, there is a good
                chance we may be able to help you get approved. Our goal is
                simple: help you get approved, get you into a reliable vehicle,
                and help you move forward with confidence.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      All Credit Types
                    </p>
                    <p className="text-sm text-gray-500">
                      Solutions tailored to your needs
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Bad Credit</p>
                    <p className="text-sm text-gray-500">
                      Financing options available
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Low Credit</p>
                    <p className="text-sm text-gray-500">
                      We work with many situations
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      No Credit / New to Canada
                    </p>
                    <p className="text-sm text-gray-500">
                      We help first-time Canadian buyers
                    </p>
                  </div>
                </div>
              </div>

              <a
                href="tel:6043588081"
                className="inline-flex items-center gap-2 text-red-600 font-semibold hover:text-red-700 transition-colors"
              >
                <Phone className="w-5 h-5" />
                Call us: (604) 358-8081
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </section>

        <LoanCalculator />

        {/* Features Grid */}
        <section className="bg-gray-900 py-24 mb-24 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-red-600 rounded-full filter blur-3xl" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-600 rounded-full filter blur-3xl" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16 scroll-reveal">
              <Badge className="mb-4 bg-white/10 text-white border-0">
                <Award className="w-4 h-4 mr-2" />
                Why Customers Choose Big Boys MotorClub
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Financing Help{" "}
                <span className="text-red-500">For Real Credit Situations</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                We focus on practical financing solutions for customers with bad
                credit, low credit, no credit, and new credit in Canada.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: Percent,
                  title: "Multiple Lenders",
                  description:
                    "We work with multiple lenders to help find financing options that fit your situation.",
                },
                {
                  icon: Clock3,
                  title: "Fast Approval",
                  description:
                    "Start your application today and get help quickly from our finance team.",
                },
                {
                  icon: Wallet,
                  title: "Stable Income Matters",
                  description:
                    "If you have a steady income or full-time job, we will work hard to help you get approved.",
                },
                {
                  icon: TrendingUp,
                  title: "Credit Rebuilding Support",
                  description:
                    "Financing a vehicle can also help you move forward and rebuild your credit over time.",
                },
                {
                  icon: PiggyBank,
                  title: "Solutions That Fit You",
                  description:
                    "We focus on finding financing that matches your budget and personal situation.",
                },
                {
                  icon: Users,
                  title: "Personal Service",
                  description:
                    "Our team takes the time to understand your situation and guide you through the process.",
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="scroll-reveal bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-red-500/50 transition-all group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Credit Building Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div className="scroll-reveal order-2 lg:order-1">
              <Badge className="mb-4 bg-green-100 text-green-700 border-0">
                <BarChart3 className="w-4 h-4 mr-2" />
                Move Forward With Confidence
              </Badge>

              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Get Approved And{" "}
                <span className="text-green-600">Keep Building Forward</span>
              </h2>

              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Many customers come to us after credit challenges or while just
                starting to build credit in Canada. Our focus is not only
                helping you get approved, but also helping you move toward a
                stronger financial future.
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <ThumbsUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Help You Get Approved
                    </h4>
                    <p className="text-gray-600">
                      We work hard to find a financing path that matches your
                      current situation.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Get You Into a Reliable Vehicle
                    </h4>
                    <p className="text-gray-600">
                      Our goal is to help you get behind the wheel of a
                      dependable vehicle.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Support Your Next Step
                    </h4>
                    <p className="text-gray-600">
                      We help customers who are rebuilding credit or creating
                      their credit history in Canada.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                <div className="flex items-center gap-3 mb-3">
                  <Zap className="w-6 h-6 text-green-600" />
                  <span className="font-bold text-gray-900">Pro Tip</span>
                </div>
                <p className="text-gray-700">
                  If you have steady income or full-time employment, be sure to
                  mention it in your application — it can help our team find the
                  right financing solution faster.
                </p>
              </div>
            </div>

            {/* Image */}
            <div className="scroll-reveal order-1 lg:order-2 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/images/credit-building.jpg"
                  alt="Credit building visualization"
                  className="w-full h-auto"
                />
              </div>

              {/* Stats Overlay */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 hidden md:block">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">
                      +60 Points
                    </p>
                    <p className="text-gray-500">Average credit increase</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="bg-gradient-to-br from-gray-50 to-gray-100 py-0 mb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 scroll-reveal">
              <Badge className="mb-4 bg-red-100 text-red-700 border-0">
                <Zap className="w-4 h-4 mr-2" />
                Simple Process
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Get Approved in{" "}
                <span className="text-red-600">3 Easy Steps</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                A simple process designed to help customers in all credit
                situations.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: "01",
                  title: "Apply Online",
                  description:
                    "Fill out our quick application and tell us a little about your situation.",
                  icon: FileCheck,
                },
                {
                  step: "02",
                  title: "We Review Your Options",
                  description:
                    "Our team works with multiple lenders to find financing options that fit your needs.",
                  icon: BadgeCheck,
                },
                {
                  step: "03",
                  title: "Drive Away",
                  description:
                    "Once approved, we help you get into a reliable vehicle and move forward with confidence.",
                  icon: Car,
                },
              ].map((item, index) => (
                <div key={index} className="scroll-reveal relative">
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow h-full">
                    <div className="text-6xl font-bold text-gray-100 absolute top-4 right-6">
                      {item.step}
                    </div>
                    <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-500 rounded-2xl flex items-center justify-center mb-6 relative z-10">
                      <item.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 relative z-10">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed relative z-10">
                      {item.description}
                    </p>
                  </div>

                  {/* Connector Arrow */}
                  {index < 2 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                      <ArrowRight className="w-8 h-8 text-red-400" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Happy Customers Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="scroll-reveal relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/images/finance-couple-keys.jpg"
                  alt="Happy customers receiving keys"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent" />
              </div>
            </div>

            {/* Content */}
            <div className="scroll-reveal">
              <Badge className="mb-4 bg-yellow-100 text-yellow-700 border-0">
                <Star className="w-4 h-4 mr-2" />
                Success Stories
              </Badge>

              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Helping Customers{" "}
                <span className="text-red-600">Through Credit Challenges</span>
              </h2>

              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                From bad credit and low credit to no credit and new-to-Canada
                applications, we help customers explore real financing options
                with respect, clarity, and support.
              </p>

              {/* Testimonial */}
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border-l-4 border-red-600">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-current"
                    />
                  ))}
                </div>
                <p className="text-gray-700 text-lg italic mb-6">
                  "I was worried I would not get approved because of my credit
                  situation, but the team at Big Boys MotorClub made the process
                  simple and encouraging. They worked hard to find an option for
                  me, and I finally got into a reliable vehicle."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="font-bold text-red-600">JS</span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Amandeep S.</p>
                    <p className="text-sm text-gray-500">Langley, BC</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>Bad Credit Welcome</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>New to Canada Welcome</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>All Credit Situations Welcome</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section
          id="apply"
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24"
        >
          <div className="scroll-reveal relative bg-gradient-to-br from-red-600 via-red-700 to-red-800 rounded-3xl overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl" />
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl" />
            </div>

            <div className="relative z-10 px-8 py-16 md:px-16 md:py-20 text-center">
              <Badge className="mb-6 bg-white/20 text-white border-0">
                <Sparkles className="w-4 h-4 mr-2" />
                Ready to Apply?
              </Badge>

              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                All Credit <span className="text-yellow-300">Welcome</span>
              </h2>

              <p className="text-xl text-white/90 max-w-2xl mx-auto mb-10">
                Bad credit, low credit, no credit, or new to Canada — if you
                have a steady income or full-time job, we may be able to help
                you get approved.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                <a
                  href="tel:6043588081"
                  className="inline-flex items-center justify-center gap-2 bg-white text-red-600 px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  <Phone className="w-5 h-5" />
                  Call (604) 358-8081
                </a>
                {/* <button className="inline-flex items-center justify-center gap-2 bg-white/20 backdrop-blur-sm text-white border-2 border-white/50 px-8 py-4 rounded-full font-bold text-lg hover:bg-white/30 transition-all">
                  <FileCheck className="w-5 h-5" />
                  Apply Online Now
                </button> */}
              </div>

              <div className="flex flex-wrap justify-center gap-8 text-white/80">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  <span>Bad Credit</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock3 className="w-5 h-5" />
                  <span>Low Credit</span>
                </div>
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  <span>No Credit / New to Canada</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="text-center mb-12 scroll-reveal">
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-0">
              <HelpCircle className="w-4 h-4 mr-2" />
              Got Questions?
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-red-600">Questions</span>
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about our financing process
            </p>
          </div>

          <div className="space-y-4 scroll-reveal">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
              />
            ))}
          </div>
        </section>

        {/* Contact Info Banner */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="scroll-reveal bg-gray-900 rounded-3xl p-8 md:p-12">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Call Us</h3>
                <p className="text-gray-400 mb-2">
                  Speak with a finance specialist
                </p>
                <a
                  href="tel:6043588081"
                  className="text-red-400 font-semibold hover:text-red-300 transition-colors"
                >
                  (604) 358-8081
                </a>
              </div>
              <div>
                <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Hours</h3>
                <p className="text-gray-400">Mon - Sat: 9AM - 8PM</p>
                <p className="text-gray-400">Sunday: 11AM - 6PM</p>
              </div>
              <div>
                <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Visit Us</h3>
                <p className="text-gray-400">20308 Logan Ave</p>
                <p className="text-gray-400">Langley, BC V3A 4L7</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

// Car icon component for How It Works section
function Car({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
      <circle cx="7" cy="17" r="2" />
      <path d="M9 17h6" />
      <circle cx="17" cy="17" r="2" />
    </svg>
  );
}

export default Finance;
